import { FC, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NotebookPen } from 'lucide-react';
import { NsKey } from 'enums/NsKey';
import { NoteProvider, useNote } from '../contexts/NoteContext';
import { NoteFormValues } from '../types/interface';
import NoteActionBar from './NoteActionBar';
import NoteList from './NoteList';
import NoteDetails from './NoteDetails';
import NoteForm from './NoteForm';

export interface NoteManagerRef {
  openAddForm: () => void;
}

export interface NoteManagerProps {
  riskId?: string;
  personId?: string;
  autoOpenForm?: boolean;
  title?: string;
}

interface NoteManagerContentProps {
  innerRef: React.MutableRefObject<NoteManagerRef | null>;
  autoOpenForm?: boolean;
  title?: string;
}

const NoteManagerContent: FC<NoteManagerContentProps> = ({ innerRef, autoOpenForm, title }) => {
  const { t } = useTranslation(NsKey.Note);
  const {
    selectedNote,
    isAddingNote,
    setAddingMode,
    createNote,
    notes,
    isLoading
  } = useNote();

  // Expose methods to parent via ref
  useEffect(() => {
    innerRef.current = {
      openAddForm: () => setAddingMode(true)
    };
  }, [innerRef, setAddingMode]);

  // Auto-open form if prop is set
  useEffect(() => {
    if (autoOpenForm) {
      setAddingMode(true);
    }
  }, [autoOpenForm, setAddingMode]);

  const handleAddNoteSave = (values: NoteFormValues) => {
    createNote(values);
  };

  const handleAddNoteCancel = () => {
    setAddingMode(false);
  };

  return (
    <div className="bg-white overflow-hidden rounded-2xl">
      {/* Action bar - full width above both panels */}
      <div className="flex-shrink-0  border-slate-200 ">
        <NoteActionBar title={title} />
      </div>

      {/* Content area - list and details side by side */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel - Notes list */}
        <div className="w-[380px] flex-shrink-0  border-slate-200  overflow-y-auto">
          <div className="p-4">
            {isLoading && notes.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8  border-primary" />
              </div>
            ) : (
              <NoteList />
            )}
          </div>
        </div>

        {/* Right panel - Note details or form */}
        <div className="flex-1  overflow-hidden">
          {isAddingNote ? (
            <NoteForm
              onSave={handleAddNoteSave}
              onCancel={handleAddNoteCancel}
            />
          ) : selectedNote ? (
            <NoteDetails />
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400">
              <div className="text-center">
                <NotebookPen size={48} className="mx-auto mb-3 opacity-50" />
                <p>
                  {notes.length === 0
                    ? t('EMPTY_STATE.DESCRIPTION')
                    : t('SELECT_NOTE')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const NoteManager = forwardRef<NoteManagerRef, NoteManagerProps>(
  ({ riskId, personId, autoOpenForm, title }, ref) => {
    const innerRef = useRef<NoteManagerRef | null>(null);

    useImperativeHandle(ref, () => ({
      openAddForm: () => innerRef.current?.openAddForm()
    }));

    return (
      <NoteProvider riskId={riskId} personId={personId}>
        <NoteManagerContent innerRef={innerRef} autoOpenForm={autoOpenForm} title={title} />
      </NoteProvider>
    );
  }
);

export default NoteManager;
